import { useState, useEffect } from "react";
import { 
  FileText, Clock, Building, Mail, Phone, Globe, CheckCircle, 
  AlertCircle, User, Briefcase, Calendar, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceRequestsManager = ({ getAuthHeader }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API}/tools/service-requests`, { 
        headers: getAuthHeader() 
      });
      setRequests(res.data.requests || []);
    } catch (error) {
      console.error("Failed to fetch service requests:", error);
      toast.error("Failed to load service requests");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.patch(
        `${API}/tools/service-requests/${requestId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Status updated");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: "bg-blue-100 text-blue-700",
      reviewing: "bg-amber-100 text-amber-700",
      contacted: "bg-purple-100 text-purple-700",
      in_progress: "bg-cyan-100 text-cyan-700",
      completed: "bg-green-100 text-green-700",
      cancelled: "bg-slate-100 text-slate-700"
    };
    return styles[status] || "bg-slate-100 text-slate-700";
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: "bg-red-100 text-red-700",
      medium: "bg-amber-100 text-amber-700",
      low: "bg-green-100 text-green-700"
    };
    return styles[priority] || "bg-slate-100 text-slate-700";
  };

  const filteredRequests = filter === "all" 
    ? requests 
    : requests.filter(r => r.status === filter);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Service Requests</h2>
          <p className="text-sm text-muted-foreground">
            {requests.length} total requests from Tool 9 (Service Request Builder)
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="reviewing">Reviewing</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'submitted').length}</p>
                <p className="text-xs text-muted-foreground">New</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'reviewing').length}</p>
                <p className="text-xs text-muted-foreground">Reviewing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'in_progress').length}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{requests.filter(r => r.status === 'completed').length}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Request List */}
      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-muted-foreground">No service requests found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <Card key={request.id} className="overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleExpand(request.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm text-brand font-semibold">
                        {request.request_number}
                      </span>
                      <Badge className={getStatusBadge(request.status)}>
                        {request.status?.replace('_', ' ')}
                      </Badge>
                      <Badge className={getPriorityBadge(request.priority_level)}>
                        {request.priority_level} priority
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4 text-slate-400" />
                        {request.contact_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building className="h-4 w-4 text-slate-400" />
                        {request.organization}
                      </span>
                      <span className="flex items-center gap-1">
                        <Globe className="h-4 w-4 text-slate-400" />
                        {request.country}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                      {request.summary}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {new Date(request.created_at).toLocaleDateString()}
                    </span>
                    {expandedId === request.id ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedId === request.id && (
                <div className="border-t bg-slate-50 p-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Contact Info */}
                    <div>
                      <h4 className="font-semibold text-sm mb-3">Contact Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-400" />
                          <a href={`mailto:${request.email}`} className="text-brand hover:underline">
                            {request.email}
                          </a>
                        </div>
                        {request.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <a href={`tel:${request.phone}`} className="text-brand hover:underline">
                              {request.phone}
                            </a>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-slate-400" />
                          <span>{request.service_type?.replace('_', ' ')} - {request.industry}</span>
                        </div>
                        {request.employees_count && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <span>{request.employees_count} employees</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Service Details */}
                    <div>
                      <h4 className="font-semibold text-sm mb-3">Service Details</h4>
                      <div className="space-y-3">
                        {request.certifications_of_interest?.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Certifications of Interest</p>
                            <div className="flex flex-wrap gap-1">
                              {request.certifications_of_interest.map((cert, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{cert}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {request.recommended_services?.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Recommended Services</p>
                            <div className="flex flex-wrap gap-1">
                              {request.recommended_services.map((svc, i) => (
                                <Badge key={i} className="bg-brand/10 text-brand text-xs">{svc}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {request.specific_requirements && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Specific Requirements</p>
                            <p className="text-sm bg-white p-2 rounded border">{request.specific_requirements}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tool Results */}
                  {request.tool_results && Object.keys(request.tool_results).length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-2">Attached Tool Results</h4>
                      <div className="flex gap-2">
                        {request.tool_results.certification_finder && (
                          <Badge variant="secondary">✓ Certification Finder</Badge>
                        )}
                        {request.tool_results.readiness_assessment && (
                          <Badge variant="secondary">
                            ✓ Readiness: {request.tool_results.readiness_assessment.readiness_level?.replace('_', ' ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Update Status:</span>
                      <Select 
                        value={request.status} 
                        onValueChange={(value) => updateStatus(request.id, value)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="reviewing">Reviewing</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <a href={`mailto:${request.email}?subject=Re: ${request.request_number}`}>
                          <Mail className="h-4 w-4 mr-1" /> Email
                        </a>
                      </Button>
                      {request.phone && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={`tel:${request.phone}`}>
                            <Phone className="h-4 w-4 mr-1" /> Call
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceRequestsManager;
